import { useQuery } from '@tanstack/react-query'
import {
  getTreasuryBalance,
  getRewardsVaultBalance,
  getStakingAdminInfo,
  validateDbcFeeClaimer,
  getEpochInfo,
  getRecentPriorityFeeStats,
  type SolanaEpochInfo,
  type PriorityFeeStats,
  type DbcFeeClaimerCheck,
  type StakingAdminInfo,
} from '@/lib/solana'
import { captureError } from '@/lib/sentry'

// SOL/USD feed ID (mirrors dextoro-launch/supabase/functions/sol-price/index.ts)
const PYTH_SOL_USD_FEED_ID =
  'ef0d8b6fda2ceba41da15d4095d1da392a0d2f8ed0c6c7bc0f4cfac8c280b56d'
const PYTH_HERMES_URL = `https://hermes.pyth.network/v2/updates/price/latest?ids[]=${PYTH_SOL_USD_FEED_ID}`

export interface PythSolUsd {
  price: number
  conf: number
  publishTime: number // unix seconds
}

async function fetchPythSolUsd(): Promise<PythSolUsd | null> {
  try {
    const res = await fetch(PYTH_HERMES_URL)
    if (!res.ok) throw new Error(`Pyth Hermes returned ${res.status}`)
    const json = await res.json()
    const parsed = json?.parsed?.[0]
    if (!parsed?.price) return null
    const expo: number = parsed.price.expo
    const priceRaw: string = parsed.price.price
    const confRaw: string = parsed.price.conf
    const publishTime: number = parsed.price.publish_time
    return {
      price: Number(priceRaw) * 10 ** expo,
      conf: Number(confRaw) * 10 ** expo,
      publishTime,
    }
  } catch (error) {
    captureError(
      error instanceof Error ? error : new Error('Pyth Hermes SOL/USD fetch failed'),
      { feature: 'infrastructure_dashboard', component: 'useTreasuryOnchain' }
    )
    return null
  }
}

export interface TreasuryOnchainData {
  treasurySol: number
  rewardsVaultSol: number
  stakingAdmin: StakingAdminInfo
  dbcConfig: DbcFeeClaimerCheck
  epoch: SolanaEpochInfo | null
  priorityFee: PriorityFeeStats | null
  pyth: PythSolUsd | null
}

/**
 * Single batched hook for the Treasury & On-Chain Health section. All RPC
 * calls run in parallel via Promise.all; each helper has its own try/catch
 * returning a safe default so a single upstream failure surfaces as a
 * card-level error rather than blanking the whole section.
 *
 * Refresh cadence: 30s refetch / 15s stale (matches useSeedbotHealth pattern).
 */
export function useTreasuryOnchain() {
  return useQuery({
    queryKey: ['treasury-onchain'],
    queryFn: async (): Promise<TreasuryOnchainData> => {
      const [
        treasurySol,
        rewardsVaultSol,
        stakingAdmin,
        dbcConfig,
        epoch,
        priorityFee,
        pyth,
      ] = await Promise.all([
        getTreasuryBalance(),
        getRewardsVaultBalance(),
        getStakingAdminInfo(),
        validateDbcFeeClaimer(),
        getEpochInfo(),
        getRecentPriorityFeeStats(),
        fetchPythSolUsd(),
      ])
      return {
        treasurySol,
        rewardsVaultSol,
        stakingAdmin,
        dbcConfig,
        epoch,
        priorityFee,
        pyth,
      }
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
  })
}
